import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useArchitectureStore } from '../../store/useArchitectureStore'; // Assuming we can get userId here or from auth context
import { Loader2, Image as ImageIcon } from 'lucide-react';

interface GeneratedImage {
    name: string;
    url: string;
    created_at: string;
}

const RecentGenerations: React.FC = () => {
    const [images, setImages] = useState<GeneratedImage[]>([]);
    const [loading, setLoading] = useState(true);
    // Ideally get real userId. For now, we might need to fetch it or pass it. 
    // If useArchitectureStore doesn't have it, we might need to use supabase.auth.getUser()
    const [userId, setUserId] = useState<string | null>(null);
    const { isProcessing, resultImage } = useArchitectureStore(); // Re-fetch when processing finishes?

    useEffect(() => {
        const getUser = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) setUserId(user.id);
        };
        getUser();
    }, []);

    const fetchImages = async () => {
        if (!userId) return;
        setLoading(true);
        try {
            // List files in the user's folder
            const { data, error } = await supabase.storage
                .from('generated-images')
                .list(userId + '/', {
                    limit: 10,
                    sortBy: { column: 'created_at', order: 'desc' },
                });

            if (error) {
                console.error('Error fetching images:', error);
                return;
            }

            if (data) {
                // Construct public URLs
                const loadedImages = data
                    .filter(file => file.name !== '.emptyFolderPlaceholder') // Filter out placeholders
                    .map(file => {
                        const { data: { publicUrl } } = supabase.storage
                            .from('generated-images')
                            .getPublicUrl(`${userId}/${file.name}`);

                        return {
                            name: file.name,
                            url: publicUrl,
                            created_at: file.created_at,
                        };
                    })
                    .slice(0, 5); // Take top 5

                setImages(loadedImages);
            }
        } catch (err) {
            console.error('Unexpected error fetching recent generations:', err);
        } finally {
            setLoading(false);
        }
    };

    // Initial Fetch when userId is set
    useEffect(() => {
        if (userId) fetchImages();
    }, [userId]);

    // Re-fetch when a new generation completes (isProcessing goes false, resultImage updates)
    useEffect(() => {
        if (!isProcessing && resultImage && userId) {
            // Small delay to ensure storage is consistent?
            setTimeout(() => fetchImages(), 1000);
        }
    }, [isProcessing, resultImage]);

    if (!userId) return null; // Don't show if not logged in

    return (
        <div className="pointer-events-auto bg-stone-900/80 backdrop-blur-md border border-stone-800 rounded-2xl p-2 flex items-center gap-2 shadow-2xl transition-all hover:bg-stone-900">
            {loading && images.length === 0 ? (
                <div className="p-2 text-stone-500">
                    <Loader2 size={16} className="animate-spin" />
                </div>
            ) : images.length === 0 ? (
                <div className="text-[10px] text-stone-500 font-medium px-2 uppercase tracking-wide">
                    No Generations
                </div>
            ) : (
                <>
                    <div className="text-[10px] text-stone-500 font-bold px-1 uppercase tracking-wider hidden md:block writing-mode-vertical rotate-180">
                        Recent
                    </div>
                    <div className="flex gap-2">
                        {images.map((img, idx) => (
                            <div
                                key={img.name}
                                className={`
                                    relative group w-12 h-12 md:w-16 md:h-16 rounded-lg overflow-hidden border border-stone-700 cursor-pointer transition-all hover:scale-105 hover:border-amber-500
                                    ${idx >= 1 ? 'hidden md:block' : 'block'} // Show only 1 on mobile, 5 on desktop
                                `}
                                onClick={() => window.open(img.url, '_blank')} // Simple view for now
                                title={new Date(img.created_at).toLocaleString()}
                            >
                                <img
                                    src={img.url}
                                    alt="Generated"
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default RecentGenerations;
